<template>
  <div class="parte-card" @click="$emit('click', item)">
    <div class="parte-card-grid">
      <div class="parte-col parte-col-id">
        <div class="parte-num">
          Nº: <strong>{{ parteNumero }}</strong>
          <span v-if="estadoParte" class="parte-estado">{{ estadoParte }}</span>
        </div>
        <div class="small text-muted">Estado ERP: {{ estadoErp }}</div>
        <div v-if="estadoSat" class="small mt-1">
          <span class="text-muted">Estado SAT: </span>
          <span :class="estadoSatClass">{{ estadoSat }}</span>
        </div>
        <div class="small text-muted">Creado: {{ creado }}</div>
        <div v-if="item.albaran" class="small mt-1">
          <span class="text-muted">Albarán </span>
          <span class="text-primary">{{ item.albaran.numero }}</span>
        </div>
      </div>

      <div class="parte-col parte-col-desc">
        <div class="parte-titulo">{{ titulo }}</div>
        <div class="small">
          <span class="text-muted">Tarea facturación:</span>
          <strong class="text-primary ml-1">{{ tareaFacturacion }}</strong>
        </div>
      </div>

      <div class="parte-col parte-col-cliente">
        <div class="parte-cliente">{{ cliente }}</div>
        <div class="small">{{ contacto }}</div>
      </div>

      <div class="parte-col parte-col-empleado">
        <div class="parte-empleado">{{ empleado }}</div>
        <div class="small">Inicio {{ fechaInicio }}</div>
      </div>

      <div class="parte-col parte-col-extra">
        <div v-if="clasificacionVisible" class="small text-muted">
          Clasificación: {{ clasificacion }}
        </div>
        <div v-if="resumenHoras > 0" class="small text-primary mt-1">
          <i class="fas fa-wrench mr-1"></i>{{ resumenHoras }} hrs. MO
          <span v-if="lineasManoObra" class="text-muted"> · {{ lineasManoObra }} línea(s)</span>
        </div>
        <div v-if="fredoCount" class="small text-info mt-1">
          <i class="fas fa-clock mr-1"></i>{{ fredoCount }} fichaje(s) FREDO
        </div>
        <div v-if="materialBadges.length || alertas.length" class="mt-1">
          <span
            v-for="(badge, idx) in materialBadges"
            :key="'mat-' + idx"
            class="badge d-inline-block mb-1 mr-1"
            :class="badge.class"
          >
            {{ badge.label }}
          </span>
          <span
            v-for="(alerta, idx) in alertas"
            :key="idx"
            class="badge d-inline-block mb-1"
            :class="getBadgeClass(alerta)"
          >
            {{ formatBadge(alerta) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  displayParteField,
  formatParteDate,
  formatCoherenciaBadge,
  getCoherenciaBadgeClass,
  getMaterialResumenBadges,
  getParteNumero,
  getParteTitulo,
  unwrapFlexy,
} from '@/util/parteSat';

export default {
  name: 'ParteListCard',
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  computed: {
    parte() {
      return this.item.parte_sat || {};
    },
    parteNumero() {
      return getParteNumero(this.item);
    },
    titulo() {
      return getParteTitulo(this.item);
    },
    estadoParte() {
      return displayParteField(this.parte.estado, null);
    },
    estadoErp() {
      return displayParteField(this.parte.estadoErp);
    },
    estadoSat() {
      return displayParteField(this.parte.estadoSat, null);
    },
    estadoSatClass() {
      const estado = this.estadoSat;
      if (!estado) return '';
      if (estado === 'Pendiente') return 'text-warning font-weight-bold';
      if (estado === 'Finalizado SAT' || estado === 'Facturado') return 'text-success font-weight-bold';
      if (estado === 'Cancelado') return 'text-danger font-weight-bold';
      return 'text-info font-weight-bold';
    },
    creado() {
      return formatParteDate(this.parte.creado || this.parte.fechaParte);
    },
    tareaFacturacion() {
      return displayParteField(this.parte.tareaFacturacion);
    },
    cliente() {
      return displayParteField(this.parte.cliente, displayParteField(this.item.albaran?.cliente));
    },
    contacto() {
      return displayParteField(this.parte.contacto);
    },
    empleado() {
      return displayParteField(
        this.parte.empleado,
        displayParteField(this.item.albaran?.jefe_equipo?.nombre)
      );
    },
    fechaInicio() {
      return formatParteDate(this.parte.fechaInicio || this.parte.fechaParte);
    },
    clasificacion() {
      const c = unwrapFlexy(this.parte.clasificacion);
      if (!c || String(c).toLowerCase() === 'sin notificar') return null;
      return c;
    },
    clasificacionVisible() {
      return !!this.clasificacion;
    },
    fredoCount() {
      return (this.item.fredo_empleados || []).length;
    },
    alertas() {
      return (this.item.coherencia && this.item.coherencia.alertas) || [];
    },
    materialAlbaran() {
      return this.item.material_albaran || null;
    },
    materialBadges() {
      return getMaterialResumenBadges(this.materialAlbaran);
    },
    resumenHoras() {
      return this.item.resumen_mano_obra?.total_horas || 0;
    },
    lineasManoObra() {
      return this.item.resumen_mano_obra?.lineas || 0;
    },
  },
  methods: {
    formatBadge: formatCoherenciaBadge,
    getBadgeClass: getCoherenciaBadgeClass,
  },
};
</script>

<style scoped>
.parte-card {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.parte-card:hover {
  border-color: #5e72e4;
  box-shadow: 0 2px 8px rgba(94, 114, 228, 0.15);
}

.parte-card-grid {
  display: grid;
  grid-template-columns: 1.1fr 1.6fr 1.2fr 1fr 1fr;
  gap: 1rem;
  align-items: start;
}

.parte-num {
  color: #2dce89;
  font-size: 0.95rem;
}

.parte-estado {
  color: #5e72e4;
  font-weight: 700;
  margin-left: 0.35rem;
}

.parte-titulo {
  font-weight: 600;
  color: #32325d;
}

.parte-cliente {
  color: #fb6340;
  font-weight: 600;
}

.parte-empleado {
  color: #5e72e4;
  font-weight: 600;
}

.badge-sin-materiales {
  background-color: #fb6340;
  color: #fff !important;
  font-weight: 600;
  font-size: 0.7rem;
}

.badge-sin-parte {
  background-color: #f5365c;
  color: #fff !important;
  font-weight: 600;
  font-size: 0.7rem;
}

.badge-activos {
  background-color: #5e72e4;
  color: #fff !important;
  font-weight: 600;
  font-size: 0.7rem;
}

.badge-consumibles {
  background-color: #11cdef;
  color: #fff !important;
  font-weight: 600;
  font-size: 0.7rem;
}

.badge-sacas {
  background-color: #8965e0;
  color: #fff !important;
  font-weight: 600;
  font-size: 0.7rem;
}

@media (max-width: 1200px) {
  .parte-card-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .parte-card-grid {
    grid-template-columns: 1fr;
  }
}
</style>
